// Modal focus trap + Esc-to-close + focus restoration.
//
// Usage:
//   import { useFocusTrap } from '@/composables/useFocusTrap'
//   const dialogRef = ref(null)
//   useFocusTrap(dialogRef, { onClose: () => emit('close') })
//
//   <div ref="dialogRef" role="dialog" aria-modal="true"> ... </div>
//
// For a persistent v-show surface, pass active: () => isOpen.value.
// returnFocus can also be a getter, so navigation can keep its new focus.
//
// What it does:
//   - On mount: remembers the previously-focused element, focuses the first
//     focusable inside the dialog (or the dialog itself if none found).
//   - Tab / Shift-Tab cycle inside the dialog instead of escaping to the
//     page body.
//   - Esc fires the supplied onClose callback (so the dialog can choose
//     whether to actually close or e.g. confirm-then-close).
//   - On unmount: restores focus to the element the user was on before
//     the dialog opened, so keyboard nav doesn't lose its place.
//
// Why not import a library: the trap we need is ~80 lines, every modern
// browser ships `focus({ preventScroll: true })`, and the alternatives
// (focus-trap, focus-trap-vue) add ~2.5 KB gzipped for behavior we
// already render.

import { onMounted, onUnmounted, nextTick, toValue, watch } from 'vue'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',')
const KEY_LISTENER_OPTIONS = { capture: true }

function getFocusables(root) {
  if (!root) return []
  // Filter out hidden + zero-sized nodes — they're focusable per the
  // selector but visually unreachable. offsetParent === null is the
  // cheapest "is this displayed" heuristic short of computed styles.
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
  )
}

export function useFocusTrap(rootRef, options = {}) {
  const { onClose, autoFocus = true, returnFocus = true, active = true } = options
  let previouslyFocused = null
  let stopWatching = null
  let generation = 0
  let trapping = false

  function onKeyDown(event) {
    const root = rootRef.value
    if (!root) return

    if (event.key === 'Escape') {
      // stopPropagation so we don't simultaneously fire e.g. a page-level
      // "Esc closes the player" listener mounted higher up.
      event.stopPropagation()
      event.preventDefault()
      if (typeof onClose === 'function') onClose()
      return
    }

    if (event.key !== 'Tab') return

    const focusables = getFocusables(root)
    if (focusables.length === 0) {
      // No focusable children — pin focus on the dialog itself so Tab
      // can't escape into the page behind.
      event.preventDefault()
      root.focus()
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    } else if (!root.contains(active)) {
      // Active element drifted outside the dialog (e.g. focus moved to
      // <body> after a click). Pull it back to the first focusable.
      event.preventDefault()
      first.focus()
    }
  }

  async function activate() {
    if (trapping) return
    trapping = true
    const currentGeneration = ++generation
    previouslyFocused = document.activeElement
    document.addEventListener('keydown', onKeyDown, KEY_LISTENER_OPTIONS)

    if (!toValue(autoFocus)) return
    // Wait one tick so the dialog's contents (incl. Teleport target) are
    // in the DOM before we query for focusables.
    await nextTick()
    if (!trapping || currentGeneration !== generation) return
    const root = rootRef.value
    if (!root) return
    const focusables = getFocusables(root)
    const target = focusables[0] || root
    if (!target.hasAttribute('tabindex') && target === root) {
      // Give the dialog itself a programmatic tabindex so .focus() works
      // when it has no focusable children.
      root.setAttribute('tabindex', '-1')
    }
    try { target.focus({ preventScroll: true }) } catch { /* jsdom / detached */ }
  }

  function deactivate() {
    if (!trapping) return
    trapping = false
    generation++
    document.removeEventListener('keydown', onKeyDown, KEY_LISTENER_OPTIONS)
    if (
      toValue(returnFocus) &&
      previouslyFocused &&
      typeof previouslyFocused.focus === 'function' &&
      document.contains(previouslyFocused)
    ) {
      try { previouslyFocused.focus({ preventScroll: true }) } catch { /* detached */ }
    }
    previouslyFocused = null
  }

  // Optional active ref/getter supports persistent, v-show overlays. Dialogs
  // mounted with v-if keep the original mount/unmount behavior by default.
  onMounted(() => {
    stopWatching = watch(() => toValue(active), (open) => {
      if (open) activate()
      else deactivate()
    }, { immediate: true, flush: 'sync' })
  })

  onUnmounted(() => {
    stopWatching?.()
    deactivate()
  })
}
